/**
 * CTA セクション。3 variants:
 *   button — ボタンのみ (シンプル)
 *   banner — 背景色付き帯 + タイトル + ボタン
 *   card   — タイトル + 説明 + ボタン (カード形式)
 */
import { MousePointerClick } from 'lucide-react';

import { TextField } from './primitives';
import type { EditorProps, RendererProps, SectionDefinition } from './types';

interface CtaButtonConfig {
  label: string;
  href: string;
}
interface CtaBannerConfig {
  title: string;
  label: string;
  href: string;
}
interface CtaCardConfig {
  title: string;
  description: string;
  label: string;
  href: string;
}

function ButtonEditor({ config, onChange, onCommit }: EditorProps) {
  const set = (patch: Partial<CtaButtonConfig>) =>
    onChange({ ...config, ...patch });
  return (
    <div className="space-y-3">
      <TextField
        label="ボタン文言"
        value={(config.label as string) ?? ''}
        onChange={(v) => set({ label: v })}
        onCommit={onCommit}
        placeholder="お問い合わせ"
      />
      <TextField
        label="リンク先 URL"
        value={(config.href as string) ?? ''}
        onChange={(v) => set({ href: v })}
        onCommit={onCommit}
        placeholder="/contact"
      />
    </div>
  );
}

function ButtonCta({ config }: RendererProps) {
  const v = config as unknown as CtaButtonConfig;
  return (
    <section className="text-center py-6">
      <a
        href={v.href || '#'}
        className="inline-block px-6 py-3 bg-[var(--accent)] text-white rounded-sm font-medium"
      >
        {v.label || 'お問い合わせ'}
      </a>
    </section>
  );
}

function BannerEditor({ config, onChange, onCommit }: EditorProps) {
  const set = (patch: Partial<CtaBannerConfig>) =>
    onChange({ ...config, ...patch });
  return (
    <div className="space-y-3">
      <TextField
        label="見出し"
        value={(config.title as string) ?? ''}
        onChange={(v) => set({ title: v })}
        onCommit={onCommit}
      />
      <TextField
        label="ボタン文言"
        value={(config.label as string) ?? ''}
        onChange={(v) => set({ label: v })}
        onCommit={onCommit}
      />
      <TextField
        label="リンク先 URL"
        value={(config.href as string) ?? ''}
        onChange={(v) => set({ href: v })}
        onCommit={onCommit}
      />
    </div>
  );
}

function BannerCta({ config }: RendererProps) {
  const v = config as unknown as CtaBannerConfig;
  return (
    <section className="bg-[var(--accent)] text-white rounded-lg p-8 text-center md:flex md:items-center md:justify-between md:text-left gap-4">
      <h3 className="text-xl md:text-2xl font-bold">{v.title || 'まずは資料請求から'}</h3>
      <a
        href={v.href || '#'}
        className="inline-block mt-4 md:mt-0 px-6 py-3 bg-white text-[var(--accent)] rounded font-semibold whitespace-nowrap"
      >
        {v.label || 'お問い合わせ'}
      </a>
    </section>
  );
}

function CardEditor({ config, onChange, onCommit }: EditorProps) {
  const set = (patch: Partial<CtaCardConfig>) =>
    onChange({ ...config, ...patch });
  return (
    <div className="space-y-3">
      <TextField
        label="見出し"
        value={(config.title as string) ?? ''}
        onChange={(v) => set({ title: v })}
        onCommit={onCommit}
      />
      <TextField
        label="説明"
        value={(config.description as string) ?? ''}
        onChange={(v) => set({ description: v })}
        onCommit={onCommit}
      />
      <TextField
        label="ボタン文言"
        value={(config.label as string) ?? ''}
        onChange={(v) => set({ label: v })}
        onCommit={onCommit}
      />
      <TextField
        label="リンク先 URL"
        value={(config.href as string) ?? ''}
        onChange={(v) => set({ href: v })}
        onCommit={onCommit}
      />
    </div>
  );
}

function CardCta({ config }: RendererProps) {
  const v = config as unknown as CtaCardConfig;
  return (
    <section className="border-2 border-[var(--accent)] rounded-xl p-8 text-center">
      <h3 className="text-2xl font-bold mb-2">{v.title || 'まずは資料請求から'}</h3>
      {v.description && (
        <p className="text-[var(--text-secondary)] mb-5 max-w-xl mx-auto">
          {v.description}
        </p>
      )}
      <a
        href={v.href || '#'}
        className="inline-block px-7 py-3 bg-[var(--accent)] text-white rounded-md font-semibold"
      >
        {v.label || 'お問い合わせ'}
      </a>
    </section>
  );
}

export const ctaDefinition: SectionDefinition = {
  type: 'cta',
  label: 'CTA',
  icon: MousePointerClick,
  description: 'お問い合わせ・資料ダウンロードなどの誘導。',
  defaultVariant: 'card',
  useCases: ['all'],
  variants: {
    button: {
      label: 'ボタン',
      description: 'シンプルなボタンのみ',
      Renderer: ButtonCta,
      Editor: ButtonEditor,
      sample: () => ({ label: 'お問い合わせ', href: '/contact' }),
    },
    banner: {
      label: 'バナー',
      description: '背景色付きの帯 + ボタン',
      Renderer: BannerCta,
      Editor: BannerEditor,
      sample: () => ({
        title: 'まずは資料をダウンロード',
        label: '無料ダウンロード',
        href: '/contact',
      }),
    },
    card: {
      label: 'カード',
      description: '見出し + 説明 + ボタンのカード型',
      Renderer: CardCta,
      Editor: CardEditor,
      sample: () => ({
        title: 'お気軽にお問い合わせください',
        description: '導入のご相談からデモまで、ご要望に合わせてご案内します。',
        label: 'お問い合わせ',
        href: '/contact',
      }),
    },
  },
};
